import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@design-system';
import { MapPin, UserCircle2 } from 'lucide-react';
import {
  postAdvisorUpdatesByAdvisorIdRequest,
  resetAdvisorSettings,
  selectAdvisorState,
  selectSessionAdvisor,
} from '@/model';
import { LicenseList } from '@/features/licenses/components/license-list';
import { useLicenses } from '@/features/licenses/hooks/use-licenses';

const addressSchema = z.object({
  streetAddress: z.string().trim().min(1, 'Street address is required'),
  apartment: z.string().optional(),
  city: z.string().trim().min(1, 'City is required'),
  state: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}$/, 'Use the 2-letter state code (e.g. CA)'),
  zipCode: z
    .string()
    .trim()
    .regex(/^\d{5}(-\d{4})?$/, 'Use a 5-digit ZIP or ZIP+4'),
});

type AddressFormValues = z.infer<typeof addressSchema>;

type Mode = 'choose' | 'address' | 'license';

interface MissingAdvisorAddressDialogProps {
  open: boolean;
  onClose: () => void;
  onResolved: () => void;
}

export function MissingAdvisorAddressDialog({
  open,
  onClose,
  onResolved,
}: MissingAdvisorAddressDialogProps) {
  const [mode, setMode] = useState<Mode>('choose');
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const { advisorSettings } = useSelector(selectAdvisorState);
  const { data: licensesData } = useLicenses();

  const activeLicense = licensesData?.licenses?.find(
    license => license.licenseId === licensesData?.activeLicenseId,
  );
  const isActiveLicenseAdvisor = !activeLicense || activeLicense.ownerType === 'ADVISOR';
  const address = advisor?.advisorBusinessAddress;
  const hasCompleteBusinessAddress = Boolean(
    address?.streetAddress && address.city && address.state && address.zipCode,
  );
  const advisorLicenseBlocked = isActiveLicenseAdvisor && !hasCompleteBusinessAddress;

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      streetAddress: '',
      apartment: '',
      city: '',
      state: '',
      zipCode: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    setMode('choose');
    setIsSavingAddress(false);
    form.reset({
      streetAddress: advisor?.advisorBusinessAddress?.streetAddress ?? '',
      apartment: advisor?.advisorBusinessAddress?.apartment ?? '',
      city: advisor?.advisorBusinessAddress?.city ?? '',
      state: advisor?.advisorBusinessAddress?.state ?? '',
      zipCode: advisor?.advisorBusinessAddress?.zipCode ?? '',
    });
  }, [open, advisor, form]);

  useEffect(() => {
    if (!isSavingAddress) return;
    if (advisorSettings.isLoading) return;
    if (advisorSettings.status === 'complete') {
      setIsSavingAddress(false);
      dispatch(resetAdvisorSettings());
      onClose();
    } else if (advisorSettings.status === 'error') {
      setIsSavingAddress(false);
      dispatch(resetAdvisorSettings());
    }
  }, [isSavingAddress, advisorSettings.status, advisorSettings.isLoading, dispatch, onClose]);

  const handleSaveAddress = (values: AddressFormValues) => {
    if (!advisor?.advisorId) return;
    setIsSavingAddress(true);
    dispatch(
      postAdvisorUpdatesByAdvisorIdRequest({
        ...advisor,
        advisorBusinessAddress: {
          advisorId: advisor.advisorId,
          addressType: 'business',
          streetAddress: values.streetAddress,
          apartment: values.apartment ?? '',
          city: values.city,
          state: values.state.toUpperCase(),
          zipCode: values.zipCode,
          country: advisor?.advisorBusinessAddress?.country ?? 'US',
        },
      } as AdvisorProfile),
    );
  };

  const titleByMode: Record<Mode, string> = {
    choose: 'Before generating this Securian illustration',
    address: 'Add your business address',
    license: 'Choose your presenter',
  };

  const descriptionByMode: Record<Mode, string> = {
    choose:
      "Securian requires a producer address on the 'Prepared by' section of the PDF. Choose how you'd like to appear.",
    address:
      "These details will appear on every Securian illustration's 'Prepared by' section. You can update them later in your advisor profile.",
    license:
      'Pick who appears as the presenter on this and future policy illustrations. You can change this later in your settings.',
  };

  const handleDialogOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isSavingAddress) onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleDialogOpenChange}
    >
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="shrink-0 p-6">
          <DialogTitle>{titleByMode[mode]}</DialogTitle>
          <DialogDescription>{descriptionByMode[mode]}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-0">
          {mode === 'choose' && (
            <div className="flex flex-col gap-3">
              <Item
                variant="outline"
                className="hover:bg-muted cursor-pointer rounded-lg"
                onClick={() => setMode('address')}
              >
                <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-full">
                  <MapPin className="size-5" />
                </div>
                <ItemContent>
                  <ItemTitle>Add my business address</ItemTitle>
                  <ItemDescription>
                    Show my name with my own business address on illustrations.
                  </ItemDescription>
                </ItemContent>
              </Item>

              <Item
                variant="outline"
                className="hover:bg-muted cursor-pointer rounded-lg"
                onClick={() => setMode('license')}
              >
                <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-full">
                  <UserCircle2 className="size-5" />
                </div>
                <ItemContent>
                  <ItemTitle>Use a different presenter</ItemTitle>
                  <ItemDescription>
                    Pick Wealth Assurance Group or a custom presenter instead.
                  </ItemDescription>
                </ItemContent>
              </Item>
            </div>
          )}

          {mode === 'address' && (
            <Form {...form}>
              <form
                id="securian-missing-address-form"
                onSubmit={form.handleSubmit(handleSaveAddress)}
                className="flex flex-col gap-4"
              >
                <FormField
                  control={form.control}
                  name="streetAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Street Address <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="123 Main Street"
                          autoComplete="address-line1"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="apartment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Apartment, suite, etc.</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Suite 200"
                            autoComplete="address-line2"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          City <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="San Francisco"
                            autoComplete="address-level2"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          State <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="CA"
                            maxLength={2}
                            autoComplete="address-level1"
                            {...field}
                            onChange={e => field.onChange(e.target.value.toUpperCase())}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="zipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          ZIP <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="94105"
                            autoComplete="postal-code"
                            {...field}
                            onChange={e => field.onChange(e.target.value.replace(/[^\d-]/g, ''))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </form>
            </Form>
          )}

          {mode === 'license' && (
            <div className="flex flex-col gap-3">
              <LicenseList hideHeader />
              {advisorLicenseBlocked && (
                <Alert
                  variant="destructive"
                  className="p-3"
                >
                  <AlertDescription className="text-xs">
                    Your own license requires a business address. Add one on the previous step, or
                    pick Wealth Assurance Group or a custom presenter above.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 p-6 sm:gap-2">
          {mode === 'choose' && (
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
          {mode === 'address' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMode('choose')}
                disabled={isSavingAddress}
              >
                Back
              </Button>
              <Button
                type="submit"
                form="securian-missing-address-form"
                disabled={isSavingAddress}
              >
                {isSavingAddress ? 'Saving…' : 'Save Address'}
              </Button>
            </>
          )}
          {mode === 'license' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMode('choose')}
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={onResolved}
                disabled={advisorLicenseBlocked}
              >
                Generate Illustration
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
